import { Stack, TextField } from '@mui/material';
import { useState } from 'react';

import type { AvailableLanguage } from '../api/business/types';
import { useLanguage } from '../i18n/LanguageContext';

import { LanguageTabs } from './LanguageTabs';
import { languageName } from './localisedText';

type LocalisedTextFieldProps = {
  label: string;
  /** The tenant's content languages, the default first. */
  languages: Array<string>;
  available: Array<AvailableLanguage>;
  /** By language code. */
  values: Record<string, string>;
  onChange: (next: Record<string, string>) => void;
  maxLength: number;
  size?: 'small' | 'medium';
  autoFocus?: boolean;
};

/**
 * One text in every content language (ADR-0010 §2): a tab per language, the default first and marked, a
 * missing translation marked. With one language it is a plain field, as before languages existed.
 */
export const LocalisedTextField = ({
  label,
  languages,
  available,
  values,
  onChange,
  maxLength,
  size = 'medium',
  autoFocus = false,
}: LocalisedTextFieldProps) => {
  const { language, t } = useLanguage();
  const [active, setActive] = useState(languages[0] ?? 'sk');
  const current = languages.includes(active) ? active : languages[0];
  const isDefault = current === languages[0];
  const field = (
    <TextField
      size={size}
      fullWidth
      autoFocus={autoFocus}
      required={isDefault}
      label={languages.length > 1 ? `${label} (${languageName(current, available, language)})` : label}
      helperText={languages.length > 1 && !isDefault ? t('languages.translationHint') : undefined}
      value={values[current] ?? ''}
      inputProps={{ maxLength }}
      onChange={(event) => onChange({ ...values, [current]: event.target.value })}
    />
  );

  if (languages.length <= 1) {
    return field;
  }

  return (
    <Stack spacing={1} sx={{ width: '100%' }}>
      <LanguageTabs
        languages={languages}
        value={current}
        onChange={setActive}
        missing={(code) => (values[code] ?? '').trim() === ''}
      />
      {field}
    </Stack>
  );
};
